import { GradebookActivityType, GradebookCellStatus } from "@axio/graphql/generated/graphql";
import type {
  CumulativeGradeCell,
  DraftWeightingCategory,
  DraftWeightingConfig,
  GradeWeightingConfigData,
  WeightingActivity,
} from "./gradebookWeighting.types";

const roundPercent = (value: number): number => Math.round(value * 100) / 100;

export const createLocalCategoryId = (): string => `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const cellMaxPoints = (cell: CumulativeGradeCell): number => {
  if (cell.maxPoints != null && Number.isFinite(cell.maxPoints) && cell.maxPoints > 0) {
    return cell.maxPoints;
  }

  return 100;
};

const isExcludedFromCalculation = (cell: CumulativeGradeCell | undefined): boolean =>
  !cell ||
  cell.status === "EXCUSED" ||
  cell.status === "IN_PROGRESS" ||
  cell.status === "UNSUBMITTED" ||
  (cell.status !== GradebookCellStatus.Missing && cell.percentageScore == null);

const categoryActivityIds = (category: DraftWeightingCategory): string[] => [
  ...category.assignmentIds,
  ...category.assessmentIds,
  ...category.projectIds,
];

export const toDraftWeightingConfig = (
  config: GradeWeightingConfigData | null | undefined,
  courseId: string,
  facultyId: string
): DraftWeightingConfig => ({
  id: config?.id ?? null,
  enabled: config?.enabled ?? false,
  courseId: config?.courseId ?? courseId,
  facultyId: config?.facultyId ?? facultyId,
  categories:
    config?.categories
      .map((category) => ({
        id: category.id,
        name: category.name,
        weight: category.weight,
        sortOrder: category.sortOrder,
        assignmentIds: [...category.assignmentIds],
        assessmentIds: [...category.assessmentIds],
        projectIds: [...category.projectIds],
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder) ?? [],
});

export const calculateWeightTotal = (config: Pick<DraftWeightingConfig, "categories">): number =>
  roundPercent(config.categories.reduce((sum, category) => sum + category.weight, 0));

export const getActivityIdsForCategoryType = (
  category: DraftWeightingCategory,
  activityType: GradebookActivityType
): string[] => {
  if (activityType === GradebookActivityType.Assignment) return category.assignmentIds;
  if (activityType === GradebookActivityType.Assessment) return category.assessmentIds;
  return category.projectIds;
};

export const isActivityInCategory = (
  category: DraftWeightingCategory,
  activityId: string,
  activityType: GradebookActivityType
): boolean => getActivityIdsForCategoryType(category, activityType).includes(activityId);

export const createDefaultWeightingCategories = (
  activities: WeightingActivity[],
  createId: () => string = createLocalCategoryId
): DraftWeightingCategory[] => [
  {
    id: createId(),
    name: "Assessments",
    weight: 50,
    sortOrder: 0,
    assignmentIds: [],
    assessmentIds: activities
      .filter((activity) => activity.activityType === GradebookActivityType.Assessment)
      .map((activity) => activity.activityId),
    projectIds: [],
  },
  {
    id: createId(),
    name: "Assignments",
    weight: 50,
    sortOrder: 1,
    assignmentIds: activities
      .filter((activity) => activity.activityType === GradebookActivityType.Assignment)
      .map((activity) => activity.activityId),
    assessmentIds: [],
    projectIds: [],
  },
];

const calculateUnweightedTotal = (cells: CumulativeGradeCell[]): number | null => {
  let earned = 0;
  let possible = 0;

  for (const cell of cells) {
    if (isExcludedFromCalculation(cell)) continue;
    const maxPoints = cellMaxPoints(cell);
    possible += maxPoints;
    earned += cell.status === GradebookCellStatus.Missing ? 0 : ((cell.percentageScore ?? 0) / 100) * maxPoints;
  }

  return possible > 0 ? roundPercent((earned / possible) * 100) : null;
};

const calculateWeightedTotal = (cells: CumulativeGradeCell[], weightingConfig: DraftWeightingConfig): number | null => {
  const cellsByActivityId = new Map(cells.map((cell) => [cell.activityId, cell]));
  let weightedSum = 0;
  let weightUsed = 0;

  for (const category of weightingConfig.categories) {
    let earned = 0;
    let possible = 0;

    for (const activityId of categoryActivityIds(category)) {
      const cell = cellsByActivityId.get(activityId);
      if (isExcludedFromCalculation(cell)) continue;
      const maxPoints = cellMaxPoints(cell);
      possible += maxPoints;
      earned += cell?.status === GradebookCellStatus.Missing ? 0 : ((cell?.percentageScore ?? 0) / 100) * maxPoints;
    }

    if (possible <= 0) continue;
    weightedSum += (earned / possible) * category.weight;
    weightUsed += category.weight;
  }

  return weightUsed > 0 ? roundPercent((weightedSum / weightUsed) * 100) : null;
};

export const calculateCumulativeTotal = (
  studentGrades: CumulativeGradeCell[],
  weightingConfig: DraftWeightingConfig | null | undefined
): number | null => {
  if (weightingConfig?.enabled) {
    return calculateWeightedTotal(studentGrades, weightingConfig);
  }

  return calculateUnweightedTotal(studentGrades);
};
